
import { useEffect, useMemo } from 'react';
import { ModuleTypeEnum, TaskWithAssignments } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useTaskEventSystem');

// ============================================================================
// EVENT TYPE DEFINITIONS
// ============================================================================

export interface TaskEventBase {
  taskId: string;
timestamp: number;
  moduleType?: ModuleTypeEnum;
  moduleId?: string;
  userId?: string;
  source?: string; // Component or hook that triggered the event
}

export interface TaskFieldUpdateEvent extends TaskEventBase {
  field: string;
  value: unknown;
  previousValue: unknown;
  isOptimistic?: boolean;
  isRollback?: boolean;
}

export interface TaskUpdateEvent extends TaskEventBase {
  updatedTask: TaskWithAssignments;
  changes: Partial<TaskWithAssignments>;
  isOptimistic?: boolean;
}

export interface TaskCreatedEvent extends TaskEventBase {
  createdTask: TaskWithAssignments;
  parentTaskId?: string;
  groupId?: string;
  sprintId?: string;
}

export interface TaskDeletedEvent extends TaskEventBase {
  deletedTask: TaskWithAssignments;
  isSoftDelete?: boolean;
}

export interface TaskBulkUpdateEvent extends TaskEventBase {
  taskIds: string[];
  updates: Partial<TaskWithAssignments>;
  successCount: number;
  failureCount: number;
  errors?: Error[];
}

export interface TaskRecurringEvent extends TaskEventBase {
  parentTaskId: string;
  newTask: TaskWithAssignments;
  instanceNumber?: number;
  recurringPattern?: Record<string, unknown>;
}

export interface TaskSubtaskEvent extends TaskEventBase {
  parentTaskId: string;
  subtask: TaskWithAssignments;
  action: 'created' | 'updated' | 'deleted';
}

export interface TaskSelectionEvent extends TaskEventBase {
  selectedTasks: TaskWithAssignments[];
  isMultiSelect?: boolean;
  action: 'select' | 'deselect' | 'clear';
}

export interface TaskDragEvent extends TaskEventBase {
  draggedTask: TaskWithAssignments;
  targetGroupId?: string;
  targetIndex?: number;
  sourceGroupId?: string;
  sourceIndex?: number;
}

export interface TaskErrorEvent extends TaskEventBase {
  error: Error;
  operation: string;
  context?: Record<string, unknown>;
}

// Union type for all task events
export type TaskEvent = 
  | TaskFieldUpdateEvent
  | TaskUpdateEvent
  | TaskCreatedEvent
  | TaskDeletedEvent
  | TaskBulkUpdateEvent
  | TaskRecurringEvent
  | TaskSubtaskEvent
  | TaskSelectionEvent
  | TaskDragEvent
  | TaskErrorEvent;

// Event type registry
export const TaskEventTypes = {
  FIELD_UPDATE: 'task:field:update',
  TASK_UPDATE: 'task:update',
  TASK_CREATED: 'task:created', 
  TASK_DELETED: 'task:deleted',
  BULK_UPDATE: 'task:bulk:update',
  RECURRING_CREATED: 'task:recurring:created',
  SUBTASK_EVENT: 'task:subtask:event',
  SELECTION_CHANGE: 'task:selection:change',
  DRAG_EVENT: 'task:drag:event',
  ERROR: 'task:error',
} as const;

export type TaskEventType = typeof TaskEventTypes[keyof typeof TaskEventTypes];

// ============================================================================
// EVENT SYSTEM OPTIONS
// ============================================================================

export interface TaskEventSystemOptions {
  /** Enable debug logging */
  debug?: boolean;
  /** Filter events by module */
  moduleFilter?: { moduleType: ModuleTypeEnum; moduleId: string };
  /** Event batching timeout (ms) */
  batchTimeout?: number;
}

// ============================================================================
// GLOBAL SHARED EVENT SYSTEM INSTANCE
// ============================================================================

// Global singleton instance that all hooks will share
let globalEventSystem: ReturnType<typeof createEventSystemInstance> | null = null;

// Create the actual event system implementation
function createEventSystemInstance(options: TaskEventSystemOptions = {}) {
  const {
    debug = false,
    moduleFilter,
    batchTimeout = 100,
  } = options;

  // Event batching for performance
  const eventBatch: TaskEvent[] = [];
  let batchTimeoutRef: NodeJS.Timeout | null = null;
  
  // Event listeners registry
  const eventListeners = new Map<TaskEventType, Set<(event: TaskEvent) => void>>();
  
  // Debug logging (logging disabled in production)
  const debugLog = (_message: string, _data?: unknown) => {
    // Debug logging intentionally disabled - enable by setting debug flag
  };

  // ============================================================================
  // CORE EVENT DISPATCH
  // ============================================================================

  const dispatch = (eventType: TaskEventType, event: TaskEvent) => {
    debugLog(`Dispatching ${eventType}`, event);
    
    // Apply module filter if specified
    if (moduleFilter && event.moduleType && event.moduleId) {
      if (event.moduleType !== moduleFilter.moduleType || event.moduleId !== moduleFilter.moduleId) {
        return;
      }
    }
    
    // Add to batch
    eventBatch.push(event);
    
    // Set up batch processing
    if (batchTimeoutRef) {
      clearTimeout(batchTimeoutRef);
    }
    
    batchTimeoutRef = setTimeout(() => {
      processBatch();
    }, batchTimeout);
    
    // Immediate processing for critical events
    if (eventType === TaskEventTypes.ERROR) {
      processBatch();
    }
  };

  // ============================================================================
  // BATCH PROCESSING
  // ============================================================================

  const processBatch = () => {
    if (eventBatch.length === 0) return;
    
    const batch = [...eventBatch];
    eventBatch.length = 0; // Clear array
    
    debugLog(`Processing batch of ${batch.length} events`);
    
    // Process each event
    batch.forEach(event => {
      const eventType = getEventType(event);
      
      // Notify all listeners for this event type
      const listeners = eventListeners.get(eventType);
      if (listeners) {
        listeners.forEach(listener => {
          try {
            listener(event);
          } catch (error) {
            logger.error(`[TaskEventSystem] Error in event listener for ${eventType}:`, error);
          }
        });
      }
    });
  };

  // ============================================================================
  // EVENT LISTENER MANAGEMENT
  // ============================================================================

  const addEventListener = (eventType: TaskEventType, listener: (event: TaskEvent) => void) => {
    if (!eventListeners.has(eventType)) {
      eventListeners.set(eventType, new Set());
    }
    eventListeners.get(eventType)!.add(listener);
    
    debugLog(`Added listener for ${eventType}. Total listeners: ${eventListeners.get(eventType)!.size}`);
    
    // Return cleanup function
    return () => {
      eventListeners.get(eventType)?.delete(listener);
      debugLog(`Removed listener for ${eventType}. Remaining listeners: ${eventListeners.get(eventType)?.size || 0}`);
    };
  };

  const removeEventListener = (eventType: TaskEventType, listener: (event: TaskEvent) => void) => {
    eventListeners.get(eventType)?.delete(listener);
  };

  // ============================================================================
  // CONVENIENCE DISPATCH METHODS
  // ============================================================================

  const dispatchFieldUpdate = (
    taskId: string,
    field: string,
    value: unknown,
    previousValue: unknown,
    options: { isOptimistic?: boolean; isRollback?: boolean; source?: string } = {}
  ) => {
    const event: TaskFieldUpdateEvent = {
      taskId,
      field,
      value,
      previousValue,
      timestamp: Date.now(),
      isOptimistic: options.isOptimistic,
      isRollback: options.isRollback,
      source: options.source,
    };
    dispatch(TaskEventTypes.FIELD_UPDATE, event);
  };

  const dispatchTaskUpdate = (
    taskId: string,
    updatedTask: TaskWithAssignments,
    changes: Partial<TaskWithAssignments>,
    options: { isOptimistic?: boolean; source?: string } = {}
  ) => {
    const event: TaskUpdateEvent = {
      taskId,
      updatedTask,
      changes,
      timestamp: Date.now(),
      moduleType: updatedTask.moduleType,
      moduleId: updatedTask.moduleId,
      isOptimistic: options.isOptimistic,
      source: options.source,
    };
    dispatch(TaskEventTypes.TASK_UPDATE, event);
  };

  const dispatchTaskCreated = (
    createdTask: TaskWithAssignments,
    options: { parentTaskId?: string; groupId?: string; sprintId?: string; source?: string } = {}
  ) => {
    const event: TaskCreatedEvent = {
      taskId: createdTask.id,
      createdTask,
      timestamp: Date.now(),
      moduleType: createdTask.moduleType,
      moduleId: createdTask.moduleId,
      parentTaskId: options.parentTaskId,
      groupId: options.groupId,
      sprintId: options.sprintId,
      source: options.source,
    };
    dispatch(TaskEventTypes.TASK_CREATED, event);
  };

  const dispatchTaskDeleted = (
    taskId: string,
    deletedTask: TaskWithAssignments,
    options: { isSoftDelete?: boolean; source?: string } = {}
  ) => {
    const event: TaskDeletedEvent = {
      taskId,
      deletedTask,
      timestamp: Date.now(),
      moduleType: deletedTask.moduleType,
      moduleId: deletedTask.moduleId,
      isSoftDelete: options.isSoftDelete,
      source: options.source,
    };
    dispatch(TaskEventTypes.TASK_DELETED, event);
  };

  const dispatchBulkUpdate = (
    taskIds: string[],
    updates: Partial<TaskWithAssignments>,
    result: { successCount: number; failureCount: number; errors?: Error[] },
    options: { source?: string } = {}
  ) => {
    const event: TaskBulkUpdateEvent = {
      taskId: taskIds[0] || '', // Use first task ID as representative
      taskIds,
      updates,
      timestamp: Date.now(),
      successCount: result.successCount,
      failureCount: result.failureCount,
      errors: result.errors,
      source: options.source,
    };
    dispatch(TaskEventTypes.BULK_UPDATE, event);
  };

  const dispatchError = (
    taskId: string,
    error: Error,
    operation: string,
    context?: Record<string, unknown>,
    options: { source?: string } = {}
  ) => {
    const event: TaskErrorEvent = {
      taskId,
      error,
      operation,
      timestamp: Date.now(),
      context,
      source: options.source,
    };
    dispatch(TaskEventTypes.ERROR, event);
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getEventType = (event: TaskEvent): TaskEventType => {
    if ('field' in event) return TaskEventTypes.FIELD_UPDATE;
    if ('createdTask' in event) return TaskEventTypes.TASK_CREATED;
    if ('deletedTask' in event) return TaskEventTypes.TASK_DELETED;
    if ('taskIds' in event) return TaskEventTypes.BULK_UPDATE;
    if ('newTask' in event) return TaskEventTypes.RECURRING_CREATED;
    if ('subtask' in event) return TaskEventTypes.SUBTASK_EVENT;
    if ('error' in event) return TaskEventTypes.ERROR;
    return TaskEventTypes.TASK_UPDATE;
  };

  // ============================================================================
  // CLEANUP
  // ============================================================================

  const cleanup = () => {
    if (batchTimeoutRef) {
      clearTimeout(batchTimeoutRef);
    }
    processBatch(); // Process any remaining events
    eventListeners.clear();
  };

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Core methods
    dispatch,
    addEventListener,
    removeEventListener,
    
    // Convenience dispatch methods
    dispatchFieldUpdate,
    dispatchTaskUpdate,
    dispatchTaskCreated,
    dispatchTaskDeleted,
    dispatchBulkUpdate,
    dispatchError,
    
    // Event types for external use
    EventTypes: TaskEventTypes,
    
    // Status
    isProcessing: () => eventBatch.length > 0,
    
    // Debug
    debugLog,
    
    // Cleanup
    cleanup,
  };
}

export function useTaskEventSystem(options: TaskEventSystemOptions = {}) {
  // Always return the same global instance
  if (!globalEventSystem) {
    // Only enable debug logging in development for initial setup
    globalEventSystem = createEventSystemInstance({ debug: false, ...options });
  }
  
  return useMemo(() => globalEventSystem!, []);
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Hook for listening to specific task events
 */
export function useTaskEventListener<T extends TaskEvent>(
  eventType: TaskEventType,
  listener: (event: T) => void,
  dependencies: unknown[] = []
) {
  const eventSystem = useTaskEventSystem();
  
  useEffect(() => {
    const cleanup = eventSystem.addEventListener(eventType, listener as (event: TaskEvent) => void);
    return cleanup;
  }, [eventSystem, eventType, ...dependencies]);
}

/**
 * Hook for task-specific event listening
 */
export function useTaskSpecificEventListener<T extends TaskEvent>(
  taskId: string,
  eventType: TaskEventType,
  listener: (event: T) => void,
  dependencies: unknown[] = []
) {
  useTaskEventListener(
    eventType,
    (event: T) => {
      if (event.taskId === taskId) {
        listener(event);
      }
    },
    [taskId, ...dependencies]
  );
}

/**
 * Hook for module-specific event listening
 */
export function useModuleEventListener<T extends TaskEvent>(
  moduleType: ModuleTypeEnum,
  moduleId: string,
  eventType: TaskEventType,
  listener: (event: T) => void,
  dependencies: unknown[] = []
) {
  useTaskEventListener(
    eventType,
    (event: T) => {
      if (event.moduleType === moduleType && event.moduleId === moduleId) {
        listener(event);
      }
    },
    [moduleType, moduleId, ...dependencies]
  );
}

export default useTaskEventSystem; 